'use client';

import { useId } from 'react';
import BrandLogo from '@/components/shared/BrandLogo';

/**
 * Full-screen card shell (ember background) for maintenance check, auth gates, etc.
 */
export function AppGateShell({ children, busy = false }) {
  return (
    <main
      className="ember-bg flex min-h-screen items-center justify-center px-4 py-10"
      role={busy ? 'status' : undefined}
      aria-live={busy ? 'polite' : undefined}
      aria-busy={busy ? 'true' : undefined}
    >
      <section className="shadow-panel-card relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-bg-card p-10 text-center sm:p-12">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-56 w-[130%] -translate-x-1/2 rounded-[100%] bg-accent-amber/15 blur-3xl" />
        <div className="pointer-events-none absolute -top-20 right-0 h-40 w-40 rounded-full bg-accent-orange/10 blur-3xl" />
        <div className="relative">{children}</div>
      </section>
    </main>
  );
}

const DEFAULT_TITLE = 'Checking status';
const DEFAULT_DESCRIPTION = 'Verifying platform availability. This only takes a moment.';

/**
 * Brand orbit spinner + copy. Gradient id is unique per mount (useId).
 */
export function BrandOrbitLoader({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
}) {
  const id = useId();
  const gradId = `orbit-loader-grad-${id.replace(/:/g, '')}`;

  return (
    <div className="mx-auto flex flex-col items-center">
      <div className="relative mb-10 flex h-28 w-28 items-center justify-center" aria-hidden>
        <div className="absolute inset-[-35%] rounded-full bg-accent-amber/15 blur-3xl" />
        <div className="absolute inset-[-15%] rounded-full bg-accent-orange/10 blur-2xl" />
        <svg
          className="relative z-0 h-full w-full animate-spin"
          viewBox="0 0 64 64"
          style={{ animationDuration: '1.15s' }}
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--accent-amber)" />
              <stop offset="100%" stopColor="var(--accent-orange)" />
            </linearGradient>
          </defs>
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            className="stroke-border"
            strokeWidth="2"
            opacity="0.45"
          />
          <circle
            cx="32"
            cy="32"
            r="28"
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="44 132"
          />
        </svg>
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <BrandLogo size="loader" variant="auto" />
        </div>
      </div>
      <h2 className="font-display text-xl font-semibold tracking-tight text-text-primary sm:text-2xl">
        {title}
      </h2>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-text-muted">{description}</p>
      <div className="mt-8 flex justify-center gap-1.5" aria-hidden>
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent-amber" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent-amber [animation-delay:120ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent-amber [animation-delay:240ms]" />
      </div>
    </div>
  );
}

/** Convenience: shell + orbit loader with custom copy (auth / maintenance check). */
export function AppAuthLoader({ title, description }) {
  return (
    <AppGateShell busy>
      <BrandOrbitLoader title={title} description={description} />
    </AppGateShell>
  );
}
